import * as React from 'react';

import {
  BoldPlugin,
  HeadingPlugin,
  ItalicPlugin,
  StrikethroughPlugin,
  UnderlinePlugin,
} from '@platejs/basic-nodes/react';
import { ParagraphPlugin, Plate, PlateContent, usePlateEditor } from 'platejs/react';

import { ChatEditorMarkdownPlugin, deserializeMarkdownWithMentions } from './ChatEditorMarkdown';
import { MentionKit } from './ChatEditorMention';
import { LinkKit, ListKit, ParagraphElement, TableKit } from '../AlineMarkdownEditor';

interface ChatMessageStaticRendererProps {
  markdown: string;
  className?: string;
}

/**
 * Renders markdown chat messages as static Plate content.
 * Supports mentions, bold, italic, underline, strikethrough, lists, links, and tables.
 *
 * MentionElements within this renderer read hydrated entities from MentionEntityContext,
 * which is provided by ChatPanel via useMentionEntitiesQuery.
 */
export function ChatMessageStaticRenderer({ className, markdown }: ChatMessageStaticRendererProps) {
  const editor = usePlateEditor({
    plugins: [
      ParagraphPlugin.withComponent(ParagraphElement),
      BoldPlugin,
      ItalicPlugin,
      UnderlinePlugin,
      StrikethroughPlugin,
      HeadingPlugin,
      ...ListKit,
      ...LinkKit,
      ...TableKit,
      ...MentionKit,
      ChatEditorMarkdownPlugin,
    ],
    value: [{ children: [{ text: '' }], type: 'p' }],
  });

  // Convert markdown to Plate value and update editor
  React.useEffect(() => {
    if (!markdown?.trim()) return;

    try {
      editor.tf.setValue(deserializeMarkdownWithMentions(editor, markdown));
    } catch (error) {
      console.error('[ChatMessageStaticRenderer] Failed to deserialize markdown:', error);
      // Fallback to plain text in a paragraph
      editor.tf.setValue([{ children: [{ text: markdown }], type: 'p' }]);
    }
  }, [markdown, editor]);

  return (
    <Plate readOnly editor={editor}>
      <PlateContent className={className} />
    </Plate>
  );
}
